import isEmpty from 'lodash/isEmpty.js'
import get from 'lodash/get.js'
import {createSelector} from 'reselect'

import api from '../../../common/api.js'
import {isPresent, isNumeric} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import CurrencyInput from '../../../common/components/CurrencyInput.js'
import ProductFilter from '../../../common/components/ProductFilter.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {
  productsSelector,
  getProductName,
  saveProductSupplier,
  ensureProductsLoaded,
  productSelector,
  setDefaultSupplier,
} from '../../../data/products.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  itemsSelector,
  poItemSKUSelector,
  supplierIDSelector,
  ensurePO,
  setPO,
} from '../../../data/pos.js'
import {getInitialSupplierValues} from '../../ProductListPage/Modals/CreatePOModal/createPOModalActions.js'
import {
  setForm,
  updateForm,
  removeForm,
  getState,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import className from '../../../common/className.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {plural} from '../../../common/components/Plural.js'
import {
  hasPOFinancialsPermissionSelector,
  saveUserSettings,
  userSettingSelector,
} from '../../../data/me.js'
import TextArea from '../../../common/components/TextArea.js'

export const MODAL_FORM = 'edit_po_item_modal'
const PO__ONLY_FROM_SUPPLIER_PRODUCT_SEARCH =
  'po.only_from_supplier_product_search'

export function onlyFromSupplierProductSearchSelector(state) {
  return userSettingSelector(state, {
    setting: PO__ONLY_FROM_SUPPLIER_PRODUCT_SEARCH,
    defaultValue: false,
  })
}

export function showEditPOItemModal(poID, poItemIDs = [], autoFocusOn = null) {
  const poItems = itemsSelector(getState(), {poID})

  autoFocusOn = autoFocusOn || (poItemIDs.length === 0 ? 'sku' : 'quantity')

  const firstPOItem = poItems.find(({id}) => id === poItemIDs[0])

  setForm(MODAL_FORM, {
    poID,
    poItemIDs,
    autoFocusOn,
    isSingleInputEdit: poItemIDs.length > 1,
    sku: null,
    quantity: String(get(firstPOItem, 'quantity') || 1),
    unit_price: String(get(firstPOItem, 'unit_price') || 0),
    discount_amount: String(get(firstPOItem, 'discount_amount') || 0),
    supplier_note: String(get(firstPOItem, 'supplier_note') || ''),
    syncUnitPrice: false,
    onlyFromSupplier: onlyFromSupplierProductSearchSelector(getState()),
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates, meta) {
  updateForm(MODAL_FORM, updates, meta)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export const errorsSelector = createSelector(
  modalFormSelector,
  ({poItemIDs, sku, quantity, unit_price, discount_amount}) => {
    const errors = {}

    if (poItemIDs.length === 0 && !sku) {
      errors.sku = 'Product is required'
    }

    if (!isNumeric(quantity)) {
      errors.quantity = 'Quantity must be a number'
    }

    if (!isPresent(quantity)) {
      errors.quantity = 'Quantity is required'
    }

    if (!isNumeric(unit_price)) {
      errors.unit_price = 'Supplier Unit Cost must be a number'
    }

    if (!isPresent(unit_price)) {
      errors.unit_price = 'Supplier Unit Cost is required'
    }

    if (!isNumeric(discount_amount)) {
      errors.discount_amount = 'Discount must be a number'
    }

    if (!isPresent(discount_amount)) {
      errors.discount_amount = 'Discount is required'
    }

    if (!isEmpty(errors)) {
      errors.preventSave = true
    }

    return errors
  },
)

async function saveEditPOItemModalSettings() {
  const {onlyFromSupplier} = modalFormSelector(getState())

  await saveUserSettings({
    [PO__ONLY_FROM_SUPPLIER_PRODUCT_SEARCH]: onlyFromSupplier,
  })
}

export async function updateSinglePOItem(poID, poItemID, params) {
  const {json: purchaseOrder} = await (poItemID
    ? api.put(
        `/purchase_order/${encodeURIComponent(poID)}/line/${poItemID}/`,
        params,
      )
    : api.post(`/purchase_order/${encodeURIComponent(poID)}/line/`, params))

  setPO(purchaseOrder)
}

export async function doSyncUnitPrice(poID, skus, supplier_price) {
  const supplierID = supplierIDSelector(getState(), {poID})

  await ensureProductsLoaded(skus)

  await Promise.all(
    skus.map(async (sku) => {
      const product = productSelector(getState(), {sku})

      const needsDefaultSet = product.suppliers.length === 0

      await saveProductSupplier(supplierID, sku, {supplier_price})

      if (needsDefaultSet) {
        await setDefaultSupplier(supplierID, sku)
      }
    }),
  )
}

export async function updatePOItem({addAnother} = {}) {
  try {
    const {
      poID,
      poItemIDs,
      sku,
      quantity,
      unit_price,
      discount_amount,
      supplier_note,
      syncUnitPrice,
      autoFocusOn,
      isSingleInputEdit,
    } = modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const params = {}
    let reallySyncUnitPrice = false

    if (poItemIDs.length === 0) {
      params.sku = sku
    }

    if (!isSingleInputEdit || autoFocusOn === 'quantity') {
      params.quantity = Number(quantity)
    }

    if (!isSingleInputEdit || autoFocusOn === 'unit_price') {
      params.unit_price = Number(unit_price)
      reallySyncUnitPrice = syncUnitPrice
    }

    if (!isSingleInputEdit || autoFocusOn === 'discount_amount') {
      params.discount_amount = Number(discount_amount)
    }

    if (!isSingleInputEdit || autoFocusOn === 'supplier_note') {
      params.supplier_note = supplier_note.trim()
    }

    await Promise.all(
      poItemIDs.length === 0
        ? [updateSinglePOItem(poID, null, params)]
        : poItemIDs.map((poItemID) =>
            updateSinglePOItem(poID, poItemID, params),
          ),
    )

    if (reallySyncUnitPrice) {
      const skus = params.sku
        ? [params.sku]
        : itemsSelector(getState(), {poID})
            .filter(({id}) => poItemIDs.includes(id))
            .map(({product}) => product.sku)

      await doSyncUnitPrice(poID, skus, params.unit_price)
    }

    await ensurePO(poID, {reload: true})

    await saveEditPOItemModalSettings()

    closeModal()

    showMessageToast(
      poItemIDs.length === 0
        ? 'Added PO item'
        : `Saved PO item${poItemIDs.length === 1 ? '' : 's'}`,
    )

    if (addAnother) {
      showEditPOItemModal(poID)
    }
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function EditPOItemModal({form}) {
  const firstItemID = form.poItemIDs[0]

  const errors = useSelector(errorsSelector)
  const firstItemSKU = useSelector((state) =>
    poItemSKUSelector(state, {
      poID: form.poID,
      poItemID: firstItemID,
    }),
  )
  const productsBySKU = useSelector(productsSelector)
  const supplierID = useSelector((state) =>
    supplierIDSelector(state, {poID: form.poID}),
  )
  const hasPOFinancialsPermission = useSelector(
    hasPOFinancialsPermissionSelector,
  )

  return (
    <ConfirmModal
      title={
        form.poItemIDs.length === 0 ? 'Add PO Item' : 'Edit PO Item Values'
      }
      modalSize="sm"
      onConfirm={() => updatePOItem()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      MiddleButtons={
        form.poItemIDs.length === 0
          ? () => (
              <ButtonPrimary
                alt
                className="margin-right-10"
                isDisabled={errors.preventSave || form.isSaving}
                isLoading={form.isSaving}
                onClick={() => updatePOItem({addAnother: true})}
              >
                Create and Add Another
              </ButtonPrimary>
            )
          : null
      }
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      <ul className="list list--no-style">
        {form.poItemIDs.length === 0 ? (
          <>
            <li className="list__item--form list__item--no-style list__item--product-filter-stacked divider--bottom">
              {form.sku ? (
                <ButtonLink
                  className="darker align-left"
                  onClick={() => updateModalForm({sku: null})}
                >
                  <div className="lh-md margin-bottom-3">
                    <strong className="fs-01 margin-right-5">
                      {getProductName(productsBySKU[form.sku])}
                    </strong>
                    <span className="unbold">({form.sku})</span>
                  </div>
                  <div className="fs-n0 lh-md text--lt-grey">Edit</div>
                </ButtonLink>
              ) : (
                <>
                  <ProductFilter
                    className="margin-bottom-15"
                    label="Select a Product to Add"
                    dropdown="EDIT_PO_ITEM_MODAL_PRODUCT_FILTER"
                    queryParams={
                      form.onlyFromSupplier
                        ? {supplier: [supplierID]}
                        : undefined
                    }
                    onSelect={(product) => {
                      const {quantity, unitPrice} = getInitialSupplierValues(
                        product,
                        supplierID,
                      )

                      updateModalForm({
                        sku: product.sku,
                        quantity,
                        unit_price: unitPrice,
                      })
                    }}
                    autoFocus={form.autoFocusOn === 'sku'}
                  />
                  <div className="wrap--v-align-inner">
                    <Checkbox
                      id="only_from_supplier"
                      label="Only show products assigned to PO supplier"
                      checked={form.onlyFromSupplier}
                      onChange={(onlyFromSupplier) =>
                        updateModalForm({onlyFromSupplier})
                      }
                    />
                  </div>
                </>
              )}
            </li>
          </>
        ) : (
          <li className="list__item--form list__item--no-style divider--bottom">
            <div className="fs-01">
              {form.poItemIDs.length === 1 ? (
                <>
                  <strong>Updating 1 item:</strong>
                  <div>{firstItemSKU}</div>
                </>
              ) : (
                <strong>Updating {form.poItemIDs.length} Items</strong>
              )}
            </div>
          </li>
        )}

        {(!form.isSingleInputEdit || form.autoFocusOn === 'quantity') && (
          <>
            <li className="list__item--form list__item--no-style fs-00 margin-bottom-3">
              <label className="fs-00" htmlFor="quantity">
                Quantity
              </label>
            </li>
            <li
              className={className`list__item list__item--form ${{
                'divider--bottom padding-bottom-15':
                  form.poItemIDs.length === 1,
              }}`}
            >
              <NumberInput
                id="quantity"
                value={form.quantity}
                onChange={(value) =>
                  updateModalForm({
                    quantity: `${value}`,
                  })
                }
                isInvalid={!!errors.quantity}
                autoFocus={
                  !!(form.sku && form.autoFocusOn === 'sku') ||
                  form.autoFocusOn === 'quantity'
                }
                min={0}
              />
              {errors.quantity && (
                <small className="error">{errors.quantity}</small>
              )}
            </li>
          </>
        )}
        {hasPOFinancialsPermission && (
          <>
            {(!form.isSingleInputEdit || form.autoFocusOn === 'unit_price') && (
              <li className="list__item list__item--form">
                <CurrencyInput
                  label="Supplier Unit Cost"
                  className="biggie margin-bottom-0"
                  width="sm"
                  id="unit_price"
                  value={form.unit_price}
                  onChange={(value) =>
                    updateModalForm({unit_price: `${value}`})
                  }
                  errorMessage={errors.unit_price}
                  autoFocus={form.autoFocusOn === 'unit_price'}
                />
                <div className="wrap--v-align-inner">
                  <Checkbox
                    labelClassName="margin-top-15"
                    label={plural(
                      form.poItemIDs.length === 0 ? 1 : form.poItemIDs.length,
                    )`Update Default Supplier Unit Cost for ${['these Products', 'the Product']}`}
                    id="sync_unit_price"
                    checked={form.syncUnitPrice}
                    onChange={(syncUnitPrice) =>
                      updateModalForm(
                        {syncUnitPrice},
                        {stickyProps: ['syncUnitPrice']},
                      )
                    }
                  />
                </div>
              </li>
            )}
            {(!form.isSingleInputEdit ||
              form.autoFocusOn === 'discount_amount') && (
              <li className="list__item list__item--form">
                <CurrencyInput
                  label="Discount"
                  className="biggie margin-bottom-0"
                  width="sm"
                  id="discount_amount"
                  value={form.discount_amount}
                  onChange={(value) =>
                    updateModalForm({discount_amount: `${value}`})
                  }
                  errorMessage={errors.discount_amount}
                  autoFocus={form.autoFocusOn === 'discount_amount'}
                />
              </li>
            )}
          </>
        )}
        {(!form.isSingleInputEdit || form.autoFocusOn === 'supplier_note') && (
          <li className="list__item list__item--form">
            <TextArea
              label="Note for Supplier"
              id="supplier_note"
              value={form.supplier_note}
              onChange={(supplier_note) => updateModalForm({supplier_note})}
              errorMessage={errors.supplier_note}
              autoFocus={form.autoFocusOn === 'supplier_note'}
            />
          </li>
        )}
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditPOItemModal, modalFormSelector)
